import { create } from 'zustand';
import { onShopifyDisconnected } from '@lib/services/shopify/connectionError';

/**
 * Infra 1.5 #22 —— <b>店铺连接状态的单一数据源</b>。
 *
 * <p>PRD #22 第四节第一条：「店铺连接状态单一数据源，前端所有页面读同一份<b>不各自缓存</b>」——
 * 并且点名 Case 1（卸载后继续用）与 Case 2（首次登录必须硬刷新）<b>都是状态不一致导致的</b>。
 * 所以这个 store 是那两个 case 的地基，也是 #23 的弹窗 / 提示条 / 就地空态三处的唯一依据。
 *
 * <p><b>不 persist。</b>连接状态是服务端的事实，缓存到 localStorage 只会让「刚重连成功」的商家
 * 看到上一次的未连接快照 —— 这正是 #22 要修的那类不一致。
 *
 * <p><b>弹窗与提示条的先后关系写在这里</b>（connect demo 定的，PRD 文字里没有）：
 * 断开瞬间<b>只弹弹窗</b>；关掉弹窗之后首页提示条才出现，然后一直留着，重连成功才消失。
 * 弹窗每次重新进入应用重弹（`dialogDismissed` 不持久化，刷新即回 false），同一次会话内不重复弹。
 */
interface ShopifyConnectionState {
  /** null = 还不知道（首屏未拉到）。不要把 null 当未连接 —— 那会在冷启动瞬间闪一下弹窗。 */
  connected: boolean | null;
  shopDomain: string | null;
  lastSyncedAt: string | null;
  /**
   * 断开的原因。
   *
   * <p><b>商家自己点 Disconnect 不该看到「连接已中断」那个弹窗</b>——那句话是在解释一件他没预料到的事。
   * 所以 `byUser` 时只进未连接状态（提示条与就地空态照常，因为确实没连），弹窗不弹。
   * 只有 `interrupted`（拦截器判定的自愈失败）才弹。
   *
   * <p>`neverConnected` 是 [Infra 1.5 #24] 补的第三个值，对应后端的 `NEVER_CONNECTED` ——
   * 这个商家<b>从来没有连过</b> Shopify（只装了挂件、只连了 WordPress、或全新账号）。
   * <b>弹窗与首页提示条都不出</b>：前者是在解释一次中断，后者的动作叫 Reconnect，
   * 对一个从没连过的人这两句都是假的。没有这个值的话，首屏把状态播进来的那一刻
   * 就会给每一个挂件商家弹一次「连接已中断」。
   */
  disconnectReason: 'interrupted' | 'byUser' | 'neverConnected' | null;
  /** 本次会话内弹窗是否已被关掉。关掉后首页提示条才出现。 */
  dialogDismissed: boolean;
  /** 重连回跳后的首次同步中 —— 首页出 loading，同步完成再正常渲染（#22 / #24）。 */
  syncing: boolean;

  markConnected: (info?: { shopDomain?: string | null; lastSyncedAt?: string | null }) => void;
  markDisconnected: (reason?: 'interrupted' | 'byUser' | 'neverConnected') => void;
  dismissDialog: () => void;
  setSyncing: (syncing: boolean) => void;
}

export const useShopifyConnectionStore = create<ShopifyConnectionState>(set => ({
  connected: null,
  shopDomain: null,
  lastSyncedAt: null,
  disconnectReason: null,
  dialogDismissed: false,
  syncing: false,

  markConnected: info =>
    set(state => ({
      connected: true,
      shopDomain: info?.shopDomain ?? state.shopDomain,
      lastSyncedAt: info?.lastSyncedAt ?? state.lastSyncedAt,
      disconnectReason: null,
      // 重连成功 → 弹窗与提示条都该消失，所以把关闭标记也清掉。
      dialogDismissed: false,
    })),

  // 幂等：已经是未连接就不再写 —— 一屏十个并发请求同时命中也只会触发一次渲染。
  // 例外：商家点 Disconnect 时要把原因改写掉，否则先被拦截器判过一次的话弹窗会补弹一个。
  markDisconnected: (reason: 'interrupted' | 'byUser' | 'neverConnected' = 'interrupted') =>
    set(state =>
      state.connected === false && state.disconnectReason === reason
        ? state
        : { connected: false, syncing: false, disconnectReason: reason }
    ),

  dismissDialog: () => set({ dialogDismissed: true }),
  setSyncing: syncing => set({ syncing }),
}));

/**
 * 把拦截器的广播接到 store 上。
 *
 * <p>在模块加载时订阅一次，不放在组件里 —— 请求可能发生在任何页面，包括没有挂任何 Shopify
 * 相关组件的页面，而 #23 的要求是「各页面不自己判断连接状态」。
 */
onShopifyDisconnected(() => {
  useShopifyConnectionStore.getState().markDisconnected();
});
